import React, { useMemo, useState, useEffect } from 'react';
import {
  MaterialReactTable,
  MRT_PaginationState,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import { Box } from '@mui/material';
import { PaymentStatusEnum } from '../../Types/Common.type';
import { Order } from '../../Types/Order.type';
import { Payment } from '../../Types/Payment.type';
import { Timestamp } from 'firebase/firestore';
import { useTheme } from '../../contexts/ThemeContext';
import './TransactionsTable.css';

type TableOrder = Omit<Order, 'created_at' | 'transacted_at' | 'payment'> & {
  created_at: string;
  transacted_at?: string;
  payment: Payment & { translated_status: string };
  clientName: string;
}

type StatusColorMap = {
  [key: string]: {
    backgroundColor: string;
    color: string;
  };
}

const statusTranslations: Record<PaymentStatusEnum, string> = {
  [PaymentStatusEnum.PAID]: "Pagado",
  [PaymentStatusEnum.ERROR]: "Error",
  [PaymentStatusEnum.PENDING]: "Pendiente",
  [PaymentStatusEnum.CANCELLED]: "Cancelado",
};

const getTranslatedStatus = (status: PaymentStatusEnum | undefined): string => {
  if (status === undefined) {
    return 'Desconocido';
  }
  return statusTranslations[status] || 'Desconocido';
};

const formatDate = (date: { seconds: number, nanoseconds: number } | undefined): string => {
  if (!date) return '';
  
  const timestamp = new Timestamp(date.seconds, date.nanoseconds);
  const dateObject = timestamp.toDate();
  
  const formattedDate = dateObject.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
  
  const formattedTime = dateObject.toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  return `${formattedDate} ${formattedTime}`;
};

const getTableOrder = (order: Order): TableOrder => {
  return {
    ...order,
    created_at: formatDate(order.created_at),
    transacted_at: order.transacted_at ? formatDate(order.transacted_at) : undefined,
    payment: {
      ...order.payment,
      translated_status: getTranslatedStatus(order.payment?.status)
    },
    clientName: `${order.client?.first_name || ''} ${order.client?.last_name || ''}`
  }
}

const getStatusColor = (status: PaymentStatusEnum | undefined, theme: 'light' | 'dark'): StatusColorMap[string] => {
  const lightThemeColors: StatusColorMap = {
    [PaymentStatusEnum.PAID]: {
      backgroundColor: '#d1f2d1',
      color: '#0f5132'
    },
    [PaymentStatusEnum.PENDING]: {
      backgroundColor: '#fff3cd',
      color: '#664d03'
    },
    [PaymentStatusEnum.ERROR]: {
      backgroundColor: '#f8d7da',
      color: '#721c24'
    },
    [PaymentStatusEnum.CANCELLED]: {
      backgroundColor: '#e9ecef',
      color: '#495057'
    }
  };

  const darkThemeColors: StatusColorMap = {
    [PaymentStatusEnum.PAID]: {
      backgroundColor: '#0f4b27',
      color: '#a7f3a7'
    },
    [PaymentStatusEnum.PENDING]: {
      backgroundColor: '#4a3c00',
      color: '#fdf050' // biyuyo yellow
    },
    [PaymentStatusEnum.ERROR]: {
      backgroundColor: '#5c1e24',
      color: '#f5b7b7'
    },
    [PaymentStatusEnum.CANCELLED]: {
      backgroundColor: '#3a3a3a',
      color: '#b3b3b3'
    }
  };

  const statusColors = theme === 'dark' ? darkThemeColors : lightThemeColors;
  const defaultColor = statusColors[PaymentStatusEnum.CANCELLED];

  if (status === undefined) {
    return defaultColor;
  }
  
  return statusColors[status] || defaultColor;
};

interface TransactionsTableProps {
  isLoading: boolean;
  initialPageSize: number;
  initialPageIndex: number;
  onPageSizeChange: (newSize: number) => void;
  onPageIndexChange: (newIndex: number) => void;
  orders: Order[];
  totalRowCount: number; // pass here the total numbers of row from the api response
}

const TransactionsTable: React.FC<TransactionsTableProps> = ({ 
  isLoading,
  initialPageSize,
  initialPageIndex,
  onPageSizeChange,
  onPageIndexChange,
  orders,
  totalRowCount
}) => {
  // Subscribe to theme changes to update badge colors
  const { theme } = useTheme();

  const tableOrders = useMemo(() => {
    return orders.map((order: Order) => getTableOrder(order));
  }, [orders]);

  const[pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: initialPageSize
  });

  useEffect(() => {
    if (pagination.pageSize !== initialPageSize) {
      onPageSizeChange(pagination.pageSize);
    }
    if (pagination.pageIndex !== initialPageIndex) {
      onPageIndexChange(pagination.pageIndex);
    }
  }, [pagination.pageSize, pagination.pageIndex, initialPageSize, initialPageIndex]);

  const columns = useMemo<MRT_ColumnDef<TableOrder>[]>(() => [
    {
      accessorKey: 'id',
      header: 'Identificador',
      size: 150
    },
    {
      accessorKey: 'clientName',
      header: 'Nombre',
      size: 150
    },
    {
      accessorKey: 'payment.total_amount',
      header: 'Monto',
      size: 120
    },
    {
      accessorKey: 'created_at',
      header: 'Fecha creación',
      size: 150
    },
    {
      accessorKey: 'transacted_at',
      header: 'Fecha transacción',
      size: 150
    },
    {
      id: 'paymentstatus',
      header: '',
      columns: [{
        accessorKey: 'payment',
        header: 'Estado',
        size: 120,
        Cell: ({ cell }: { cell: any }) => {
          const payment = cell.getValue();
          const status = payment.translated_status;
          const colors = getStatusColor(payment.status, theme); 
          return (
          <Box
          component="span"
          sx={{
            borderRadius: '16px',
            backgroundColor: colors.backgroundColor,
            padding: '6px 12px',
            color: colors.color,
            fontSize: '12px',
            fontWeight: '600',
            border: `1px solid ${colors.color}`,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            whiteSpace: 'nowrap'
          }}
          >
            {status}
          </Box>
        )}
      }]
    },
  ], [theme]); // Recalculate when theme changes

  const table = useMaterialReactTable({
    data: tableOrders,
    columns,
    state: {
      pagination,
      isLoading,
    },
    onPaginationChange: setPagination,
    enablePagination: true,
    manualPagination: true,
    rowCount: totalRowCount,
    localization: {
      rowsPerPage: 'Filas por página',
    },
  });

  return (
    <div className="orders-table-container">
      <MaterialReactTable table={table} />
    </div>
  );
};

export default TransactionsTable; 